import React, { useRef, useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import {AddBoxOutlined, IosShareRounded} from '@mui/icons-material';
import { ScanQRIcon } from '../assets/constants/Icons';
import { useTranslation, Trans } from 'react-i18next';
import ButtonComponent from '../components/ButtonComponent';
import { CustomBoxInput } from '../components/CustomBoxInput';
import ShowToast from '../components/ToastComponent';
import { useHistory } from 'react-router-dom';
import { ALPHANUM_REGEX, ASSET_RENTAL_USE_CASE, BUTTON_PRIMARY, BUTTON_WARNING, LUGGAGE_USE_CASE, SUCCESS, TOAST_ERROR, WINPOD_BY_WINTECH_ALT_TEXT } from '../assets/constants/Constants';
import {ACCESS_LOCKER_BANK, LOCKER_DETAILS, LOGIN} from '../assets/constants/PageList';
import { useTheme } from '@mui/material';
import CustomQRCodeReader from '../components/CustomQRCodeReader';
import { checkIfAssignmentIsProperlyConfigured, extractDomain, getDomainAndLockerBankInfoFromQRCodeData, setDomainAndLockerBankIdInLocalStorage, getPathForNextPage, applyBackendLanguageConfig, buildAddress, buildLockerDetailsData, redirectToLockerDetails, getUnitLabelParams } from '../lib/Utils';
import { getDomainAndLockerBankIdFromShortCode, getTenantAndAssignmentSettings } from '../api/api';
import CustomDialog from '../components/CustomDialog';
import DuroltLogoIcon from "../assets/images/durolt_logo_new.svg";
// import wintechIcon from '../assets/images/wintech-winpod-logo.png';
import wintechIcon from '../assets/images/wintech-winpod-logo-no-border.png';
import LockerMapPreview from '../components/LockerMapPreview';
import { getSecureItemFromSpecificStorage, storeSecureItemInSpecifiedStorage } from '../lib/BrowserStorageAccessMiddleware';
import { LOCAL_STORAGE, LOCKER_BANK_ADDRESS, LOCKER_SLUG, LS_LOCKER_BANK_INFO } from '../assets/constants/BrowserStorageKeys';
import { RenderTabs } from '../components/RenderTabsComponent';
import CircularProgressLoader from '../components/CircularProgressLoader';
import { getDeferredInstallPrompt, clearDeferredInstallPrompt, subscribeToInstallPrompt } from '../lib/InstallPromptManager';


const HomePage = () => {
    const { t } = useTranslation()
    const [installAppPrompt, setInstallAppPrompt] = useState(getDeferredInstallPrompt());
    const [showInstallAppModal, setShowInstallAppModal] = useState(false);
    const [inputValue, setInputValue] = useState(Array(6).fill(''))
    const inputRefs = useRef([]);
    const [showQRCodeReader, setShowQRCodeReader] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [disabled, setDisabled] = useState(true)
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogContentText, setDialogContentText] = useState('');
    const history = useHistory()
    const theme = useTheme()
    const colorScheme = theme.palette.mode
    const [tabValue, setTabValue] = useState(0);
    const [isFirstTab, setIsFirstTab] = useState(true); 
    const [isSecondTab, setIsSecondTab] = useState(true);
    const [firstTabLabel] = useState(t('scan_to_book_title'));
    const [secondTabLabel] = useState(t('text_enter_code_to_book'));
    const lockerBanckInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const unitLabelParams = getUnitLabelParams(lockerBanckInfo?.LockerBankType, t);
    const [boxesEnable, setBoxesEnable] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(t('pleaseWaitText'));
    
    const handleTabChange = (newValue) => {
        setTabValue(newValue);
        if (newValue === 0) {
            resetInputValue();
            setErrorMessage();
            setBoxesEnable(false);
            setDisabled(true);
        } else {
            setBoxesEnable(true);
        }
    };

    useEffect(() => {
        return subscribeToInstallPrompt(setInstallAppPrompt);
    }, []);

    const resetInputValue = () => {
        setInputValue(Array(6).fill(''));
    };


    const handleChange = (event, index) => {
        const tempVal = [...inputValue];
        let value = event.target.value;

        // Convert to uppercase if the value is an alphabet
        if (isNaN(value) && value.length === 1) {
            value = value.toUpperCase();
        }

        tempVal[index] = value;
        setInputValue(tempVal);

        if (value.length === 1 && index < inputValue.length - 1) {
            inputRefs.current[index + 1].focus();
        }

        const isValid = tempVal.every(digit => digit !== '');
        const codeInput = tempVal.join('');
        let errorMessage = '';

        if (!isValid) {
            errorMessage = t('warning_msg_fill_all_inputs');
        } else if (!ALPHANUM_REGEX.test(codeInput)) {
            errorMessage = t('warning_msg_alphanum_valid_input', unitLabelParams);
        }

        setErrorMessage(errorMessage);
        setDisabled(!isValid || !ALPHANUM_REGEX.test(codeInput));
    };


    const handleSubmit = async () => {
        try {
                let codeInput = inputValue.join('')
                const responseJson = await getDomainAndLockerBankIdFromShortCode(codeInput)
                if (responseJson.status === SUCCESS) {
                    let lockerBankId = responseJson?.data?.locker_bank_id || null; // temporary locker bank id, we need an API to get the locker bank id and domain
                    let domain = extractDomain(responseJson?.data?.domain)
                    let domainAndLockerBankInfoObject = {
                        "domain": domain,
                        "lockerBankId": lockerBankId
                    }
                    setDomainAndLockerBankIdInLocalStorage(domainAndLockerBankInfoObject)
                    if (lockerBankId) {
                        getSettingsValidateAndRedirectUser(lockerBankId);
                    }
                }
        } catch (error) {
            resetInputValue();
            setDisabled(true);
            console.error("HomePage.js - handleSubmitLockerId", error)
        }
    }

    const handleOpenQRCodeReader = () => {
        setShowQRCodeReader(true)
    }

    const qrReaderScanHandler = async (qrCodeData) => {
        try {
            if (qrCodeData && qrCodeData.length > 0) {
                setLoadingMessage(t('Getting_Lockers', unitLabelParams));
                setIsLoading(true);
                setShowQRCodeReader(false);
                const domainAndLockerBankInfoObject = getDomainAndLockerBankInfoFromQRCodeData(qrCodeData);
                const rawValue = qrCodeData?.[0]?.rawValue;
                
                if (domainAndLockerBankInfoObject) {
                    setDomainAndLockerBankIdInLocalStorage(domainAndLockerBankInfoObject);
                    await getSettingsValidateAndRedirectUser(domainAndLockerBankInfoObject?.lockerBankId);
                } else {
                    try {
                        if (rawValue) {
                            const validatedUrl = new URL(rawValue);
                            const response = await fetch(validatedUrl.toString(), {
                                method: 'GET',
                                redirect: 'follow',
                            });

                            const domainAndLockerBankInfoObject = getDomainAndLockerBankInfoFromQRCodeData(response?.url);
                            if(domainAndLockerBankInfoObject){
                                setDomainAndLockerBankIdInLocalStorage(domainAndLockerBankInfoObject);
                                await getSettingsValidateAndRedirectUser(domainAndLockerBankInfoObject?.lockerBankId);
                            }
                            return;
                        }
                    } catch (fallbackRedirectError) {
                        console.error('HomePage.js - qrReaderScanHandler fallback redirect', fallbackRedirectError);
                        if (rawValue) {
                            window.location.href = rawValue;
                        }
                        setIsLoading(false);
                        return;
                    }
                    setIsLoading(false);
                    ShowToast(t('error_msg_scanned_qr_not_valid', unitLabelParams), TOAST_ERROR)
                }
            } else {
                setIsLoading(false);
                ShowToast(t('pleaseScanAgain'), TOAST_ERROR)
            }
        } catch (error) {
            setIsLoading(false);
            console.error("HomePage.js - qrReaderScanHandler", error)
        }
    }

    const getSettingsValidateAndRedirectUser = async (lockerBankId) => {
        try {
            const response = await getTenantAndAssignmentSettings(lockerBankId);
            applyBackendLanguageConfig(response);

            if (response.status === SUCCESS) {
                const lockerslug = response?.data?.locker_bank?.slug;
                const lockerBankAddress = buildAddress(response?.data?.locker_bank);
                const tempResponse = response?.data?.locker_bank;
                const lokcerCurrency = response?.data?.locker_assignments[0]?.currency || '';
                storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LOCKER_SLUG, lockerslug);
                storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, LOCKER_BANK_ADDRESS, lockerBankAddress);
                if (response?.data?.locker_assignments.length > 1) {
                    history.push({
                        pathname: '/assignments',
                        state: {
                            tenantAndAssignmentSettings: response?.data
                        }
                    });
                }
                else if (response?.data?.locker_assignments.length === 1) {
                    const isAssignmentProperlyConfigured = await checkIfAssignmentIsProperlyConfigured(response?.data);
                    
                    if (isAssignmentProperlyConfigured === true) {

                        const templockerData = buildLockerDetailsData(tempResponse, lockerBankAddress, response?.data?.tenant_settings?.domain_instance_address);
                        // check here if Dropoff is enable then redirect to login page else redirect to locker details page
                        if(response?.data?.locker_assignments[0]?.usecase === LUGGAGE_USE_CASE){
                            const redirectionPath = getPathForNextPage(ACCESS_LOCKER_BANK, LOCKER_DETAILS);
                            await redirectToLockerDetails(history, 'push', templockerData, lokcerCurrency, redirectionPath);
                        }else{
                                const redirectionPath = getPathForNextPage(ACCESS_LOCKER_BANK, LOGIN);
                                history.push({
                                pathname: redirectionPath
                            });
                        
                        }
                    }
                    else {
                        setIsLoading(false);
                        resetInputValue();
                        setDialogContentText( t(isAssignmentProperlyConfigured?.errorMessage, getUnitLabelParams(tempResponse?.bank_type, t)) );
                        setDialogVisible(true);
                    }
                }
                else {
                    setIsLoading(false);
                    resetInputValue();
                    setDialogContentText( t('noLockerAssignments', getUnitLabelParams(tempResponse?.bank_type, t)) );
                    setDialogVisible(true);
                }
            } else {
                setIsLoading(false);
                ShowToast(t('something_went_wrong_text'), TOAST_ERROR);
            }
        } catch (error) {
            setIsLoading(false);
            resetInputValue();
            setDisabled(true);
            console.error('Error while getting settings and validation: ', error);
        }
    }

    const handleCloseModal = () => {
        setDisabled(true)
        setDialogVisible(false)
    }

    const handleInstallAppButtonClick = async () => {
        const userAgent = navigator.userAgent;
        const isAppleDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isSafariBrowser = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

        if (isAppleDevice && isSafariBrowser) {
            setShowInstallAppModal(true);
        }
        else if (installAppPrompt) {
            try {
                installAppPrompt.prompt();

                clearDeferredInstallPrompt();
            } catch (error) {
                console.error('Install app prompt error: ', error);
            }
        }
        else if (window.matchMedia('(display-mode: standalone)').matches) {
            alert(t('Already_Installed'));
        }
        else {
            alert(t('Install_Not_Available'));
        }
    };

    const renderInstallAppModalContent = () => {
        return (
            <div className="modal-content">
                <p className="install-text">{t('ios_install_instructions_title')}</p>
                <div className="instructions-list">
                    <p className="instruction">
                        <Trans
                            i18nKey="ios_install_step_1"
                            components={{
                                b: <b />,
                                shareIcon: <IosShareRounded className="instruction-icon" />,
                            }}
                        />
                    </p>
                    <p className="instruction">
                        <Trans
                            i18nKey="ios_install_step_2"
                            components={{
                                b: <b />,
                                addIcon: <AddBoxOutlined className="instruction-icon" />,
                            }}
                        />
                    </p>
                    <p className="instruction">
                        <Trans i18nKey="ios_install_step_3" components={{ b: <b /> }} />
                    </p>
                </div>
            </div>
        );
    };

    const RenderQRoption = ()=>{
        return(
            <>
            <div className='Container-ScanQR-Text' onClick={handleOpenQRCodeReader}>
                <div className='Sub-Container-ScanQR-QR'>
                    <ScanQRIcon/>
                </div>
                <div className='Sub-Container-ScanQR-Text'>
                    <h1 className='heading'>{t('scan_to_book_title')}</h1>
                    <p className='paragraph'>{t('scan_to_book_description', unitLabelParams)}</p>
                </div>
            </div>
            </>
        )
    }

    return (
        <div className='home-page' maxWidth="xs">
            <div className="main-container">
                {isLoading && <CircularProgressLoader message={loadingMessage} />}
                {showQRCodeReader ?
                    <div className="qr-code-reader">
                        <CustomQRCodeReader
                            handleOnScan={qrReaderScanHandler}
                        />
                        <p className='qr-code-heading'>
                            {t('accessLockerBankByQR', unitLabelParams)}
                        </p>
                        <ButtonComponent handleClick={() => setShowQRCodeReader(false)} title={'button_cancel'} type={BUTTON_WARNING} />
                    </div>
                    :
                    <>
                        <div className="logo_style">
                            <img src={DuroltLogoIcon} width={180} height={60} alt="Durolt Logo" />
                            {/* <img  width={240}  alt={WINPOD_BY_WINTECH_ALT_TEXT} src={wintechIcon} /> */}
                        </div>
                        <div className='Container-text'>
                            <p className={`heading ${colorScheme === 'dark' ? 'dark-text' : ""}`} style={{fontSize:16, marginTop:-10}}>
                            Powered by Durolt
                            </p>
                        </div>
                        
                        <div className='input-tab-bar-container'>
                            <RenderTabs
                                tabs={[
                                    { enabled: isFirstTab, label: firstTabLabel },
                                    { enabled: isSecondTab, label: secondTabLabel },
                                ]}
                                tabValue={tabValue}
                                onChange={handleTabChange}
                                t={t}
                            />
                        </div>
                        {boxesEnable ? 
                        <>
                            <div className="input-box-view">
                                <CustomBoxInput inputValue={inputValue} handleChange={handleChange} inputRefs={inputRefs} error={!!errorMessage} handleSubmitEvent={handleSubmit} disabled={disabled} customClass='input-box-placeholder' />
                                {errorMessage && <Typography className="error-message">{errorMessage}</Typography>}
                            </div> 
                            <ButtonComponent handleClick={handleSubmit} title={t('continueBtnText')} customClass='' type={'default'} buttonId='continue-button' disabled={disabled}/>
                        </>
                        : 
                            RenderQRoption()
                        } 
                        <div className='locker-map-container'>
                            <LockerMapPreview />                        
                        </div>
                        {lockerBanckInfo &&
                        <ButtonComponent 
                            handleClick={()=>{history.push('/login-page')}} 
                            title={'button_login'} 
                            type={'iceBlue'} 
                            buttonId='login-button'
                        /> }
                        <p className="version-text">
                            {t('appVersionText')} {process.env.REACT_APP_RELEASE_VERSION}
                        </p>
                    </>

                }
                <CustomDialog
                    dialogId='error-dialog'
                    dialogVisible={dialogVisible}
                    handleAccept={handleCloseModal}
                    dialogTitle={t('page_title_error')}
                    dialogContentText={t(dialogContentText)}
                    buttonTwoTitle={t('button_ok')}
                    buttonProps={{ buttonTwo: { type: BUTTON_PRIMARY } }}
                />
                <CustomDialog
                    dialogId='install-app-instructions-modal'
                    dialogVisible={showInstallAppModal}
                    dialogTitle={t('Install_App')}
                    children={renderInstallAppModalContent}
                    buttonTwoTitle={t('button_ok')}
                    buttonProps={{buttonTwo: {type: BUTTON_PRIMARY}}}
                    handleAccept={() => setShowInstallAppModal(false)}
                    onClose={() => setShowInstallAppModal(false)}
                />
            </div>
        </div>
    );
}

export default HomePage;
